import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, signInWithPopup, signOut, signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, deleteUser, reauthenticateWithCredential, EmailAuthProvider, updatePassword } from "firebase/auth";
import { auth, googleProvider } from "../firebase";
import { ref, set, get, remove } from "firebase/database";
import { database } from "../firebase";

const AuthContext = createContext({
  user: null,
  loading: true,
  loginWithGoogle: () => {},
  loginWithEmail: () => {},
  registerWithEmail: () => {},
  logout: () => {},
  deleteAccount: () => {},
  verifyPassword: () => {},
  updateUserProfile: () => {},
  updateUserPassword: () => {},
  sendResetCode: () => {},
  verifyResetCode: () => {},
  resetPasswordWithCode: () => {},
  isAdmin: false,
  isCustomer: false
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const userRef = ref(database, `users/${currentUser.uid}`);
          const snapshot = await get(userRef);
          
          if (!snapshot.exists()) {
            const newUserData = {
              uid: currentUser.uid,
              email: currentUser.email,
              name: currentUser.displayName,
              role: "user",
              createdAt: new Date().toISOString(),
            };
            await set(userRef, newUserData);
            setUser({ ...currentUser, ...newUserData });
          } else {
            const userData = snapshot.val();
            setUser({ ...currentUser, ...userData });
          }
        } catch (error) {
          console.error("Auth user data fetch error:", error);
          setUser(currentUser);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const loginWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const currentUser = result.user;

      const userRef = ref(database, `users/${currentUser.uid}`);
      const snapshot = await get(userRef);
      let userData;

      if (!snapshot.exists()) {
        userData = {
          uid: currentUser.uid,
          email: currentUser.email,
          name: currentUser.displayName,
          role: "user",
          createdAt: new Date().toISOString(),
        };
        await set(userRef, userData);
      } else {
        userData = snapshot.val();
      }

      setUser({ ...currentUser, ...userData });
    } catch (error) {
      console.error("Google 로그인 실패:", error);
    }
  };

  const loginWithEmail = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const currentUser = result.user;

      const userRef = ref(database, `users/${currentUser.uid}`);
      const snapshot = await get(userRef);
      let userData;

      if (!snapshot.exists()) {
        userData = {
          uid: currentUser.uid,
          email: currentUser.email,
          name: currentUser.displayName || email.split('@')[0],
          role: "user",
          createdAt: new Date().toISOString(),
        };
        await set(userRef, userData);
      } else {
        userData = snapshot.val();
      }

      setUser({ ...currentUser, ...userData });
      return { success: true };
    } catch (error) {
      console.error("이메일 로그인 실패:", error);
      let errorMessage = "로그인에 실패했습니다.";
      
      switch (error.code) {
        case 'auth/invalid-email':
          errorMessage = "유효하지 않은 이메일 형식입니다.";
          break;
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          errorMessage = "이메일 또는 비밀번호가 일치하지 않습니다.";
          break;
        case 'auth/too-many-requests':
          errorMessage = "너무 많은 로그인 시도가 있었습니다. 잠시 후 다시 시도해주세요.";
          break;
        default:
          errorMessage = error.message;
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const registerWithEmail = async (email, password, name) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const currentUser = result.user;

      // Firebase Auth 프로필에 이름 업데이트
      await updateProfile(currentUser, {
        displayName: name
      });

      const userData = {
        uid: currentUser.uid,
        email: currentUser.email,
        name: name,
        role: "user",
        createdAt: new Date().toISOString(),
      };

      await set(ref(database, `users/${currentUser.uid}`), userData);
      setUser({ ...currentUser, ...userData });
      return { success: true };
    } catch (error) {
      console.error("이메일 회원가입 실패:", error);
      let errorMessage = "회원가입에 실패했습니다.";
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = "이미 가입되어 있는 이메일입니다.";
          break;
        case 'auth/invalid-email':
          errorMessage = "유효하지 않은 이메일 형식입니다.";
          break;
        case 'auth/weak-password':
          errorMessage = "비밀번호가 너무 취약합니다. (6자 이상)";
          break;
        default:
          errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error("로그아웃 실패:", error);
    }
  };

  const deleteAccount = async () => {
    if (!auth.currentUser) return { success: false, error: "로그인 상태가 아닙니다." };
    
    try {
      const userRef = ref(database, `users/${auth.currentUser.uid}`);
      await remove(userRef); 
      await deleteUser(auth.currentUser); 
      setUser(null);
      return { success: true };
    } catch (error) {
      console.error("회원 탈퇴 실패:", error);
      let errorMessage = "탈퇴 처리 중 오류가 발생했습니다.";
      if (error.code === 'auth/requires-recent-login') {
        errorMessage = "보안을 위해 다시 로그인한 후 탈퇴를 진행해 주세요.";
      }
      return { success: false, error: errorMessage };
    }
  };

  const verifyPassword = async (password) => {
    if (!auth.currentUser || !auth.currentUser.email) return { success: false, error: "인증 정보가 없습니다." };
    
    try {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
      await reauthenticateWithCredential(auth.currentUser, credential);
      return { success: true };
    } catch (error) {
      console.error("비밀번호 확인 실패:", error);
      let errorMessage = "비밀번호가 일치하지 않습니다.";
      if (error.code === 'auth/too-many-requests') {
        errorMessage = "보안을 위해 잠시 후 다시 시도해주세요.";
      }
      return { success: false, error: errorMessage };
    }
  };

  const updateUserProfile = async (updates) => {
    if (!auth.currentUser) return { success: false, error: "로그인 상태가 아닙니다." };

    try {
      // 1. Firebase Auth 프로필 업데이트 (이름)
      if (updates.name) {
        await updateProfile(auth.currentUser, {
          displayName: updates.name
        });
      }

      // 2. Realtime Database 업데이트
      const userRef = ref(database, `users/${auth.currentUser.uid}`);
      const snapshot = await get(userRef);
      const currentDbData = snapshot.val() || {};

      const cleanData = {
        ...currentDbData,
        ...updates,
        updatedAt: new Date().toISOString()
      };

      await set(userRef, cleanData);

      // 3. 상태 업데이트
      setUser(prev => ({ ...prev, ...updates }));
      return { success: true };
    } catch (error) {
      console.error("프로필 업데이트 실패:", error);
      return { success: false, error: error.message };
    }
  };

  const updateUserPassword = async (newPassword) => {
    if (!auth.currentUser) return { success: false, error: "로그인 상태가 아닙니다." };

    try {
      await updatePassword(auth.currentUser, newPassword);
      return { success: true };
    } catch (error) {
      console.error("비밀번호 변경 실패:", error);
      let errorMessage = "비밀번호 변경에 실패했습니다.";
      if (error.code === 'auth/requires-recent-login') {
        errorMessage = "보안을 위해 다시 로그인하거나 비밀번호 재확인 후 시도해주세요.";
      } else if (error.code === 'auth/weak-password') {
        errorMessage = "비밀번호가 너무 취약합니다. (6자 이상)";
      }
      return { success: false, error: errorMessage };
    }
  };

  const sendResetCode = async (email) => {
    try {
      const resetUrl = "https://initiatepasswordreset-hxxheppfsq-uc.a.run.app";
      const response = await fetch(resetUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || "인증코드 발송에 실패했습니다.");
      }
      return { success: true };
    } catch (error) {
      console.error("인증코드 발송 실패:", error);
      return { success: false, error: error.message || "인증코드 발송에 실패했습니다. 이메일 주소를 확인해주세요." };
    }
  };

  const resetPasswordWithCode = async (email, code, newPassword) => {
    try {
      const resetUrl = "https://adminresetpassword-hxxheppfsq-uc.a.run.app";
      const emailKey = process.env.REACT_APP_EMAIL_API_KEY || "om8B2QikaJvIImlhlXTw";

      const response = await fetch(resetUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-API-Key": emailKey },
        body: JSON.stringify({ email, code, newPassword })
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || "비밀번호 재설정에 실패했습니다.");
      }

      return { success: true, message: result.message };
    } catch (error) {
      console.error("비밀번호 재설정 실패:", error);
      return { success: false, error: error.message };
    }
  };

  const verifyResetCode = async (email, code) => {
    try {
      const verifyUrl = "https://verifyresetcode-hxxheppfsq-uc.a.run.app";
      const emailKey = process.env.REACT_APP_EMAIL_API_KEY || "om8B2QikaJvIImlhlXTw";

      const response = await fetch(verifyUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-API-Key": emailKey },
        body: JSON.stringify({ email, code })
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || "인증코드 확인에 실패했습니다.");
      }

      return { success: true };
    } catch (error) {
      console.error("인증코드 확인 실패:", error);
      return { success: false, error: error.message };
    }
  };

  return (
    <AuthContext.Provider
      value={{ 
        user, 
        loading, 
        loginWithGoogle, 
        loginWithEmail,
        registerWithEmail,
        logout,
        deleteAccount,
        verifyPassword,
        updateUserProfile,
        updateUserPassword,
        sendResetCode,
        verifyResetCode,
        resetPasswordWithCode,
        isAdmin: user?.role === "admin",
        isCustomer: user?.role === "customer"
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
